import * as React from 'react';
import { Component, inject, deps, observer } from '../lib/component';
import { LoadingOverlay } from '../components/loading/overlay';
import { important, percent, px, rem, viewWidth } from 'csx';
import { classes, media, style } from 'typestyle';
import { LayoutContent } from '../components/layout/content';
const PrismicDOM = require('prismic-dom');
import { centerCenter, horizontal, start, vertical } from 'csstips';
import {PageHeader} from '../components/page-header';
import {ButtonPill} from '../components/button-pill';
import {colorSlate, colorWhite} from '../theme/color';
import MainWrapper from '../components/main-wrapper';

export interface FeaturesPageProps {
  i18n?: {
    loading: string;
  }
}

@inject(deps) @observer
export class FeaturesPage extends Component<FeaturesPageProps> {
  render() {
    const { i18n = FeaturesPage.i18n } = this.props;
    const model = this.props.model.page.features;

    const onSignUp = this.props.model.user.user
      ? null
      : this.handleSignUp;

    return (
      <MainWrapper className={FeaturesPage.styles.container}>
        <LoadingOverlay loading={model.loading} />
        <PageHeader coverImage={model.cover_image}>
          <h1>{model.loading ? i18n.loading : model.page_title}</h1>
        </PageHeader>
        {!model.loading && (
          <LayoutContent innerClassName={FeaturesPage.styles.inner}>
            <div className={FeaturesPage.styles.content}>
              <div className={classes(FeaturesPage.styles.section, FeaturesPage.styles.strapSection)}>
                <div className={FeaturesPage.styles.strapPlayer} dangerouslySetInnerHTML={{ __html: decodeURIComponent(model.video) }} />
              </div>
              <div className={classes(FeaturesPage.styles.section, FeaturesPage.styles.strapSection)}>
                <h3 className={FeaturesPage.styles.strapHeading}>
                  <img className={FeaturesPage.styles.strapIcon} src={model.icon_rosette} alt={model.strap_title}/>
                  <span>{model.strap_title}</span>
                </h3>
                <div dangerouslySetInnerHTML={{ __html: PrismicDOM.RichText.asHtml(model.strap_content) }}/>
              </div>
            </div>
            <div className={FeaturesPage.styles.benefits}>
              <div className={FeaturesPage.styles.benefitsRow}>
                <div className={classes(FeaturesPage.styles.section, FeaturesPage.styles.benefitsSection)}>
                  <img className={FeaturesPage.styles.benefitsIcon} src={model.icon_disc} />
                  <div dangerouslySetInnerHTML={{ __html: PrismicDOM.RichText.asHtml(model.features_1) }}/>
                </div>
                <div className={classes(FeaturesPage.styles.section, FeaturesPage.styles.benefitsSection)}>
                  <img className={FeaturesPage.styles.benefitsIconLast} src={model.icon_bulb} />
                  <div dangerouslySetInnerHTML={{ __html: PrismicDOM.RichText.asHtml(model.features_2) }}/>
                </div>
              </div>
              <div className={FeaturesPage.styles.benefitsRow}>
                <div className={classes(FeaturesPage.styles.section, FeaturesPage.styles.benefitsSection)}>
                  <img className={FeaturesPage.styles.benefitsIcon} src={model.icon_friends} />
                  <div dangerouslySetInnerHTML={{ __html: PrismicDOM.RichText.asHtml(model.features_3) }}/>
                </div>
                <div className={classes(FeaturesPage.styles.section, FeaturesPage.styles.benefitsSection)}>
                  <img className={FeaturesPage.styles.benefitsIconLast} src={model.icon_lightning} />
                  <div dangerouslySetInnerHTML={{ __html: PrismicDOM.RichText.asHtml(model.features_4) }}/>
                </div>
              </div>
            </div>
            <div className={FeaturesPage.styles.subtitle}>
              <h2>{model.subtitle}</h2>
            </div>
            {onSignUp && (
              <div className={FeaturesPage.styles.ctaContainer}>
                <ButtonPill onClick={onSignUp} bouncing={true} className={FeaturesPage.styles.cta}>
                  {model.cta}
                </ButtonPill>
              </div>
            )}
          </LayoutContent>
        )}
      </MainWrapper>
    )
  }

  handleSignUp = () => {
    this.props.controller.onboarding.openSignUpModal('/youtube');
  }

  static i18n = {
    loading: 'Loading...',
  };

  static styles = {
    container: style({
      minHeight: percent(100),
      flexGrow: 1,
      background: colorSlate.darken(0.10).toString(),
      color: colorWhite.toString(),
    }),
    inner: style(
      {
        minHeight: px(440),
        width: percent(100),
        maxWidth: px(1280),
        margin: '0 auto',
        padding: '0 0 4vw',
        background: important(colorSlate.darken(0.10).toString()),
        color: important(colorWhite.toString()),
        $nest: {
          '& h2': {
            margin: 0
          },
          '& h3': {
            margin: 0
          }
        }
      },
      media({ minWidth: 480 }, {
        padding: '0 0 6vw'
      }),
      media({ minWidth: 1280 }, {
        padding: '0 0 50px'
      }),
    ),
    content: style(
      {
        ...vertical,
        padding: viewWidth(6)
      },
      media({ minWidth: 480 }, {
        padding: viewWidth(4)
      }),
      media({ minWidth: 720 }, {
        ...horizontal,
        ...start
      }),
      media({ minWidth: 1280 }, {
        padding: px(50)
      })
    ),
    strapHeading: style(
      {
        fontSize: rem(1.25)
      },
      media({ minWidth: 1280 }, {
        fontSize: px(20)
      })
    ),
    section: style(
      { 
        position: 'relative',
        flex: 1
      }
    ),
    strapSection: style(
      {
        $nest: {
          '&:first-child': {
            marginBottom: viewWidth(6)
          }
        }
      },
      media({ minWidth: 480 }, {
        $nest: {
          '&:first-child': {
            marginBottom: viewWidth(4)
          }
        }
      }),
      media({ minWidth: 720 }, {
        $nest: {
          '&:first-child': {
            marginRight: viewWidth(4),
            marginBottom: 0
          }
        }
      }),
      media({ minWidth: 1280 }, {
        $nest: {
          '&:first-child': {
            marginRight: px(50),
            marginBottom: 0
          }
        }
      })
    ),
    strapPlayer: style(
      {
        position: 'relative',
        width: percent(100),
        paddingBottom: percent(50),
        $nest: {
          '& iframe': {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            height: percent(100),
            width: percent(100)
          }
        }
      }
    ),
    strapIcon: style(
      {
        width: viewWidth(15),
        margin: '0 0 6vw 6vw',
        float: 'right'
      },
      media({ minWidth: 480 }, {
        width: viewWidth(11),
        margin: '0 0 1vw 1vw'
      }),
      media({ minWidth: 1280 }, {
        width: px(140)
      })
    ),
    benefits: style(
      {
        ...vertical,
        padding: '0 6vw',
      },
      media({ minWidth: 480 }, {
        padding: viewWidth(4)
      }),
      media({ minWidth: 1280 }, {
        padding: '0 50px'
      })
    ),
    benefitsRow: style(
      {

      },
      media({ minWidth: 720 }, {
        ...horizontal,
        marginBottom: viewWidth(2)
      })
    ),
    benefitsSection: style(
      {
        ...horizontal,
        ...start,
        $nest: {
          '& ul': {
            margin: 0
          }
        }
      },
      media({ maxWidth: 720 }, {
        marginBottom: viewWidth(6),
        $nest: {
          '&:nth-child(2n)': {
            flexDirection: 'row-reverse'
          },
        }
      }),
      media({ minWidth: 720 }, {
        $nest: {
          '&:first-child': {
            marginRight: viewWidth(4),
            marginBottom: 0
          }
        }
      }),
      media({ minWidth: 1280 }, {
        $nest: {
          '&:first-child': {
            marginRight: px(50),
            marginBottom: 0
          }
        }
      })
    ),
    benefitsIcon: style(
      {
        flexShrink: 0,
        width: viewWidth(15)
      },
      media({ minWidth: 480, maxWidth: 719 }, {
        width: viewWidth(10)
      }),
      media({ minWidth: 720, maxWidth: 1299 }, {
        width: viewWidth(6)
      }),
      media({ minWidth: 1280 }, {
        width: px(80)
      })
    ),
    benefitsIconLast: style(
      {
        flexShrink: 0,
        width: viewWidth(15)
      },
      media({ minWidth: 480, maxWidth: 719 }, {
        width: viewWidth(10)
      }),
      media({ maxWidth: 720 }, {
        marginLeft: viewWidth(10)
      }),
      media({ minWidth: 720, maxWidth: 1299 }, {
        width: viewWidth(6)
      }),
      media({ minWidth: 1280 }, {
        width: px(80)
      })
    ),
    subtitle: style(
      {
        padding: viewWidth(6),
        $nest: {
          '& h2': {
            textAlign: 'center',
            fontSize: rem(2)
          }
        }
      },
      media({ minWidth: 480 }, {
        padding: viewWidth(4)
      }),
      media({ minWidth: 1280 }, {
        padding: px(50)
      })
    ),
    ctaContainer: style(
      {
        ...centerCenter,
        padding: '0 6vw',
        paddingTop: important(0)
      },
      media({ minWidth: 480 }, {
        padding: '0 4vw'
      }),
      media({ minWidth: 1280 }, {
        padding: '0 50px'
      })
    ),
    cta: style(
      {
        fontSize: rem(1.5),
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        textAlign: 'center',
        whiteSpace: 'normal',
        maxWidth: percent(100),
      }
    )
  }
}